import { useRef } from 'react';
import { format, parse } from 'date-fns';

import type { APIUser } from 'Models/models';
import type { CSSProperties } from 'react';

import Avatar from 'Components/UI_Library/Avatar';
import DropDown from 'Components/UI_Library/Dropdowns/DropDown';
import Button from 'Components/UI_Library/FormElements/Button';
import CountryInput from 'Components/UI_Library/FormElements/CountryInput';
import DatePicker from 'Components/UI_Library/FormElements/DatePicker';
import GenderInput from 'Components/UI_Library/FormElements/GenderInput';
import MobilePhoneInput from 'Components/UI_Library/FormElements/MobilePhoneInput';
import TextInput from 'Components/UI_Library/FormElements/TextInput';
import Loader from 'Components/UI_Library/LoadingIndicators/Loader';
import LoadingBar from 'Components/UI_Library/LoadingIndicators/LoadingBar';
import classes from './UserForm.module.css';

type Props = {
    className?: 'userForm';
    update?: (user: APIUser) => void;
    user?: APIUser;
    initialLoad?: boolean;
    changeAvatar?: (file: FileList | null) => void;
    avatar?: string;
    loadingAvatar?: boolean;
    viewOnly?: boolean;
    /** In some cases we want to enable editing of the non-editable fields (e.g. when a user has been invited, and needs to update their user profile) */
    enableNonEditableFields?: boolean;
};

const UserForm = (props: Props) => {
    const avatarUpload = useRef<HTMLInputElement>(null);

    const uploadAvatar = () => {
        if (avatarUpload && avatarUpload.current) avatarUpload.current.click();
    };

    /** update the mobile number as user updates country. Replaces the country code in the mobile number (if it exists) */
    const updateMobile = (country: { countryCode: string }) => {
        const countryAreaCode = props.user?.countryAreaCode || '';
        const mobile = props.user?.mobile;
        if (countryAreaCode && mobile?.startsWith(countryAreaCode)) {
            return mobile.replace(countryAreaCode, country.countryCode);
        }
        return mobile;
    };

    const updateUser = (newUserData?: Partial<APIUser>) => {
        if (props.update && props.user) {
            props.update({ ...props.user, ...newUserData });
        }
    };

    const dateFormat = 'dd/MM/yyyy';

    return (
        <div className={`${classes.userForm} ${props.className ? classes[props.className] : ''}`}>
            {props.initialLoad ? (
                <div className={classes.loadingContainer}>
                    <LoadingBar />
                </div>
            ) : (
                <div className={classes.container}>
                    <div className={classes.profilePicture}>
                        <div>
                            <div>
                                <input
                                    type="file"
                                    className={classes.avatarInput}
                                    ref={avatarUpload}
                                    accept="image/jpeg,image/jpg,image/png"
                                    onChange={(e) => {
                                        props?.changeAvatar && props.changeAvatar(e.target.files);
                                        console.log(e.target.files);
                                    }}
                                />
                                {props.loadingAvatar ? (
                                    <Loader type="medium-blue" />
                                ) : (
                                    <>
                                        {(props.avatar || props.user?.id) && (
                                            <Avatar user={props.user} noCacheAvatar={props.avatar} size={75} />
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                        {props.changeAvatar && (
                            <Button
                                type="tertiary"
                                onClick={() => uploadAvatar()}
                                loaderType="in-button blue"
                                disabled={props.viewOnly}
                            >
                                Change profile picture
                            </Button>
                        )}
                    </div>
                    <div className={classes.flexedInput}>
                        <TextInput
                            label={<LabelWithLink label="First name" showLink={!props.enableNonEditableFields} />}
                            placeholder="Jane"
                            marginBottom="8px"
                            value={props.user?.firstName}
                            onChange={(e: { target: { value: string } }) =>
                                props.enableNonEditableFields && updateUser({ firstName: e.target.value })
                            }
                            disabled={!props.enableNonEditableFields}
                        />
                        <TextInput
                            label={<LabelWithLink label="Last name" showLink={!props.enableNonEditableFields} />}
                            placeholder="Doe"
                            marginBottom="8px"
                            value={props.user?.lastName}
                            onChange={(e: { target: { value: string } }) =>
                                props.enableNonEditableFields && updateUser({ lastName: e.target.value })
                            }
                            disabled={!props.enableNonEditableFields}
                        />
                    </div>
                    <div className={classes.flexedInput}>
                        <TextInput
                            label={<LabelWithLink label="Email" />}
                            placeholder="jane.doe@company.com"
                            marginBottom="8px"
                            value={props.user?.email}
                            disabled
                            onChange={(e: { target: { value: string } }) => updateUser({ email: e.target.value })}
                        />

                        <CountryInput
                            selected={{ name: props.user?.country, code: props.user?.countryCode }}
                            disabled={props.viewOnly}
                            select={(value: { name: string; code: string; countryCode: string }) =>
                                updateUser({
                                    country: value?.name,
                                    countryCode: value?.code,
                                    countryAreaCode: value?.countryCode,
                                    mobile: updateMobile(value),
                                })
                            }
                            label={
                                <>
                                    Country of residence<span>*</span>
                                </>
                            }
                        />
                    </div>
                    <div className={classes.flexedInput}>
                        <MobilePhoneInput
                            label={
                                <>
                                    Mobile number<span>*</span>
                                </>
                            }
                            placeholder={`${props.user?.countryAreaCode || '+'}000000000`}
                            countryCode={props.user?.countryAreaCode}
                            value={props.user?.mobile}
                            onChange={(e: { target: { value: string } }) => updateUser({ mobile: e.target.value })}
                            disabled={props.viewOnly}
                        />
                        <div>
                            <DropDown
                                label="How did you find Paytron?"
                                disabled={props.viewOnly}
                                makeSelection={(_, value) => {
                                    updateUser({ referral: value?.option as APIUser['referral'] });
                                }}
                                selected={props.user?.referral || 'Choose an option'}
                                items={[
                                    { option: 'Referral' },
                                    { option: 'LinkedIn ad' },
                                    { option: 'Newsletter' },
                                    { option: 'News article' },
                                    { option: 'Contacted by our team' },
                                    { option: 'Invited' },
                                    { option: 'Other' },
                                ]}
                            />
                        </div>
                    </div>
                    <div className={classes.flexedInput}>
                        <DatePicker
                            label={
                                <LabelWithLink
                                    label="Date of birth"
                                    style={{ marginBottom: 8 }}
                                    showLink={!props.enableNonEditableFields}
                                />
                            }
                            dateFormat={dateFormat}
                            disabled={!props.enableNonEditableFields}
                            onSelect={(date) =>
                                props.enableNonEditableFields && updateUser({ dateOfBirth: format(date, dateFormat) })
                            }
                            onChange={(date) => updateUser({ dateOfBirth: format(date, dateFormat) })}
                            selected={
                                props.user?.dateOfBirth ? parse(props.user.dateOfBirth, dateFormat, new Date()) : null
                            }
                        />
                        <GenderInput
                            label="Gender"
                            disabled={props.viewOnly}
                            selected={props.user?.gender}
                            select={(value: string) => updateUser({ gender: value })}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

const LabelWithLink = ({
    label,
    style,
    showLink = true,
}: {
    label: string;
    style?: CSSProperties;
    showLink?: boolean;
}) => (
    <p className={classes.labelWithLink} style={style}>
        {label}
        {showLink && (
            <a target="_blank" href="https://www.paytron.com/contact" rel="noreferrer">
                Contact us to change
            </a>
        )}
    </p>
);

export default UserForm;
